import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,   SectionTitle, Image, CorrectionBox, Note,  TableContainer,  Result,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider, Separator, ColoredText} from '../../../../components/course/CourseComponents';
import repereImage from '../repere.jpg';
import distImage from '../dist.jpg';

const Chapitre3Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Ajouter un score et un timer.
        </p>
      </InfoBox>      

      <p>Maintenant que le cercle se déplace quand on clique dessus, on va ajouter un score et un timer pour créer une vraie expérience de jeu.</p>

      <Separator type="text" text="Ajout du score" color="gray-darker" size="md" />
      <SectionTitle>🏆 1/ Ajout du score</SectionTitle>

      <p>On va maintenant ajouter un score.</p>
      <p>Pour cela, il faut créer une variable score, l'incrémenter à chaque fois que le joueur clique sur le cercle, et l'afficher à chaque frame.<br/>
      Pour l'affichage, on aura besoin des fonctions p5.js suivantes :</p>

      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>textSize(taille)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit la taille du texte<br/>- Paramètre 1 : taille du texte</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>textAlign(align)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Définit l'alignement du texte<br/>- Paramètre 1 : alignement (LEFT, CENTER, RIGHT)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>text(message, x, y)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche du texte<br/>- Paramètre 1 : texte à afficher<br/>- Paramètre 2 : position en x<br/>- Paramètre 3 : position en y</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <Note type="exercice" title="🧪 Affiche le score :">
        - Crée une variable <InlineCode>score</InlineCode> en haut de ton code, initialisée à 0 :
        <CodeBlock language="javascript">
{`let score = 0;`}
        </CodeBlock>
        
        - Incrémente ce score quand le joueur clique sur le cercle, c'est-à-dire dans le if de la fonction <InlineCode>mousePressed()</InlineCode> :
        <CodeBlock language="javascript">
{`score = score + 1;`}
        </CodeBlock>

        - Affiche le score à chaque rafraîchissement dans la fonction <InlineCode>draw()</InlineCode> :
        <ul className="list-disc ml-6 mt-2 mb-2">
          <li>change la couleur du texte pour écrire en turquoise</li>
          <li>choisis la taille du texte</li>
          <li>aligne le texte à gauche (car il sera affiché à gauche de l'écran)</li>
          <li>affiche le texte en haut à gauche du canvas</li>
        </ul>
        <CodeBlock language="javascript">
{`// Score
fill(242, 177, 67);
textSize(30);
textAlign(LEFT);
text("Score: " + score, 10, 30);`}
        </CodeBlock>

<br/>
        <Result>         ▶️ Le score s'affiche à gauche de l'écran et s'incremente à chaque clic sur le cercle.        </Result>

        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`// Jeu 1 : Améliore ta visée
let cercle_rayon = 50;
let cercle_x = 0;
let cercle_y = 0;
let score = 0;

function setup() {
  createCanvas(600, 600);
  setCirclePosition();
}

function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(cercle_x, cercle_y, cercle_rayon * 2);

  // Score
  fill(242, 177, 67);
  textSize(30);
  textAlign(LEFT);
  text("Score: " + score, 10, 30);
}

function mousePressed() {
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    setCirclePosition();
    score = score + 1;
  }
}

function setCirclePosition() {
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}`}
        </CodeBlock>
      </CorrectionBox>
      </Note>

      <Separator type="text" text="Ajout d'un timer" color="gray-darker" size="md" />
      <SectionTitle>⏰ 2/ Ajout d'un timer</SectionTitle>

      <p>On va ajouter un timer de 20 secondes pour créer un vrai gameplay : le joueur aura 20 secondes pour cliquer sur le cercle le plus de fois possible.</p>
      <p>Comme pour le score, il faut créer une variable numérique pour le timer.<br/>
      Ensuite, on va décrémenter ce timer et l'afficher à l'écran.</p>

      <Note type="tip" title="🤔 Mais on le décrémente de combien ?">
        On veut que le timer représente des secondes, donc on doit le décrémenter de 1 chaque seconde.<br/><br/>
        Mais attention : la fonction <InlineCode>draw()</InlineCode> est appelée 60 fois par seconde.<br/>
        Il faudrait donc décrémenter le timer de 1/60 = 0.016 à chaque frame.<br/>
        En effet, en une seconde, on va décrémenter le timer de 60 * 0.016 ≈ 1 environ.<br/>
        <CodeBlock>timer = timer - 0.016</CodeBlock>
        <strong>MAIS</strong> que se passe-t-il si ton ordinateur n'est pas assez rapide pour afficher 60 FPS ?<br/>
        Imaginons que ton jeu tourne à 30 FPS : en 1 seconde, le timer sera décrémenté de 30 * 0.016 = 0.5s.<br/>
        Ce n'est pas bon...
      </Note>

      <Note type="" title="💡 La solution : deltaTime">
        Heureusement, il existe une variable dans les jeux vidéo qui s'appelle <InlineCode>deltaTime</InlineCode>. Elle contient le temps écoulée depuis le dernier <InlineCode>draw()</InlineCode>.<br/><br/>
        • À 60 FPS → deltaTime ≈ 0.016 seconde<br/>
        • À 30 FPS → deltaTime ≈ 0.033 seconde<br/><br/>
        Il suffit de retrancher deltaTime à <InlineCode>timer</InlineCode> à chaque frame, pour obtenir un vrai timer en secondes, quelque soit ton nombre de FPS.<br/><br/>

        <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Variable</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>deltaTime</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Durée écoulée depuis le dernier draw, en millisecondes.</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>
      <br/>
        La variable deltaTime de p5.js est en millisecondes, donc on divise par 1000 pour la convertir en secondes.<br/>
        <CodeBlock>timer = timer - deltaTime / 1000;</CodeBlock>
        💡 Je sais, c'est la partie la plus compliquée du code 😅
      </Note>
      <br/>
      <Note type="exercice" title="🧪 Ajout du Timer :">
        - Crée une variable <InlineCode>timer = 20</InlineCode> en haut de ton code :
        <CodeBlock language="javascript">
{`let timer = 20;`}
        </CodeBlock>

        - Dans <InlineCode>draw()</InlineCode>, décrémente le timer et affiche-le en haut à droite :
        <ul className="list-disc ml-6 mt-2 mb-2">
          <li>décrémente le timer avec deltaTime / 1000</li>
          <li>change la couleur du texte pour écrire en turquoise</li>
          <li>aligne le texte à droite (car il sera affiché à droite de l'écran)</li>
          <li>affiche le texte en haut à droite du canvas. Utilise <InlineCode>ceil(timer)</InlineCode> pour arrondir le timer à l'entier supérieur et ne pas avoir un nombre à virgule.</li>
        </ul>
        <CodeBlock language="javascript">
{`// Timer
timer = timer - deltaTime / 1000;
fill(242, 177, 67);
textAlign(RIGHT);
text("Temps: " + ceil(timer), 590, 30);`}
        </CodeBlock>

        - Enfin, toujours dans <InlineCode>draw()</InlineCode>, si timer &lt;= 0, alors :
        <ul className="list-disc ml-6 mt-2 mb-2">
          <li>on le remet à 0 pour éviter qu'il devienne négatif</li>
          <li>on affiche "gameover" dans la console</li>
        </ul>
        <CodeBlock language="javascript">
{`// Gameover
if (timer <= 0) {
  timer = 0;
  print("gameover");
}`}
        </CodeBlock>
        <br/>
        <Result>▶️ Tu devrais voir un joli timer qui représente les secondes, et un "gameover" quand il arrive à 0.</Result>
        <CorrectionBox>
        <CodeBlock language="javascript" showCopyButton={true}>
{`let timer = 20;

function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(cercle_x, cercle_y, cercle_rayon * 2);

  // Score
  fill(242, 177, 67);
  textSize(30);
  textAlign(LEFT);
  text("Score: " + score, 10, 30);

  // Timer
  timer = timer - deltaTime / 1000;
  fill(242, 177, 67);
  textAlign(RIGHT);
  text("Temps: " + ceil(timer), 590, 30);

  // Gameover
  if (timer <= 0) {
    timer = 0;
    print("gameover");
  }
}`}
        </CodeBlock>
      </CorrectionBox>

      </Note>

      <Separator type="text" text="Ajout d'un status" color="gray-darker" size="md" />
      <SectionTitle>🎮 3/ Ajout d'un status</SectionTitle>

      <p>Enfin, ajoutons une variable status (de type chaîne de caractères) pour savoir si le jeu :</p>
      <ul className="list-disc ml-6 mb-4">
        <li>n'a pas encore commencé</li>
        <li>est en cours</li>
        <li>ou est fini</li>
      </ul>

      <Note type="exercice" title="🧪 Status du jeu :">
        - Crée une variable <InlineCode>status</InlineCode> initialisée à "start" en haut de ton code :
        <CodeBlock language="javascript">
{`let status = "start"; // ou "play" ou "gameover"`}
        </CodeBlock>

        - Passe la valeur à "play" quand le joueur clique.<br/>
        On s'en servira pour lancer le timer dans la prochaine étape.<br/>
        Dans <InlineCode>mousePressed()</InlineCode> :
        <CodeBlock language="javascript">
{`if (status == "start") status = "play";`}
        </CodeBlock>

        - Puis, passe la valeur à "gameover" quand le timer arrive à 0, en remplaçant <InlineCode>print("gameover")</InlineCode> par :
        <CodeBlock language="javascript">
        <ColoredText color="red">- print("gameover");</ColoredText>{"\n"}
        <ColoredText color="green">+ status = "gameover";</ColoredText>
        </CodeBlock>

        - Affiche la valeur de <InlineCode>status</InlineCode> à la fin de <InlineCode>draw()</InlineCode> pour vérifier que ça marche :
        <CodeBlock language="javascript">
{`print(status);`}
        </CodeBlock>
<br/>
        <Result>▶️ Tu devrais voir la valeur de status changer en temps réel dans la console.</Result>
      </Note>

      <Note type="info" title="👉 Cette variable status va nous servir dans les prochaines étapes pour :">
        • lancer le jeu quand le joueur commence à cliquer,<br/>
        • et afficher un écran de fin quand le timer est à 0.
      </Note>

      <Separator type="text" text="Code final" color="gray-darker" size="md" />

      <Note type="tip" title="✅ Code final">
        Si tout s'est bien passé, tu devrais avoir un code du genre :
      </Note>

      <CodeBlock language="javascript">
{`// Jeu 1 : Améliore ta visée
let cercle_rayon = 50;
let cercle_x = 0;
let cercle_y = 0;
let score = 0;
let timer = 20;
let status = "start"; // ou "play" ou "gameover"

function setup() {
  createCanvas(600, 600);
  setCirclePosition();
}

function draw() {
  background(9, 26, 52);

  // Cercle
  fill(34, 174, 220);
  circle(cercle_x, cercle_y, cercle_rayon * 2);

  // Score
  fill(242, 177, 67);
  textSize(30);
  textAlign(LEFT);
  text("Score: " + score, 10, 30);

  // Timer
  timer = timer - deltaTime / 1000;
  fill(242, 177, 67);
  textAlign(RIGHT);
  text("Temps: " + ceil(timer), 590, 30);

  // Gameover
  if (timer <= 0) {
    timer = 0;
    status = "gameover";
  }

  print(status);
}

function mousePressed() {
  let distance = dist(mouseX, mouseY, cercle_x, cercle_y);
  if (distance < cercle_rayon) {
    setCirclePosition();
    score = score + 1;
  }
  if (status == "start") status = "play";
}

function setCirclePosition() {
  cercle_x = random(50, 550);
  cercle_y = random(50, 550);
}`}
      </CodeBlock>

      <Note type="info" title="🔜 Prochaine étape :">
        Améliorer l'interface utilisateur avec des écrans de début et de fin !
      </Note>
   
    </>
  );
};

export default Chapitre3Content; 